import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AlertTriangle, Lock, TrendingUp, HelpCircle, PackageMinus, ReceiptText, ShieldCheck, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtEuro, riskMeta } from "@/lib/okkio";
import Logo from "@/components/Logo";

const Metric = ({ icon: Icon, n, label, testid }) => (
  <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 flex items-start gap-3" data-testid={testid}>
    <span className="w-11 h-11 rounded-xl bg-[#3157F6]/10 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#3157F6]" strokeWidth={1.9} />
    </span>
    <div>
      <p className="font-heading text-3xl font-semibold text-[#0F172A] leading-none">{n}</p>
      <p className="text-sm text-[#64748B] mt-1 leading-tight">{label}</p>
    </div>
  </div>
);

export default function Teaser({ teaser, eligibility, onUnlock, unlocking }) {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const risk = riskMeta[teaser.livello_rischio] || riskMeta.medio;
  const locked = Array.from({ length: Math.max(teaser.num_criticita_bloccate, 3) });
  const punti = teaser.num_criticita_totali || 0;
  const conLimiti = eligibility?.stato === "idoneo_con_limiti";
  const nonConfront = teaser.num_non_confrontabili || 0;
  const prezziAnomali = teaser.num_incongruenze_prezzo || 0;
  const prezzoMetric = prezziAnomali > 0
    ? { n: prezziAnomali, label: "Prezzi da verificare" }
    : (nonConfront > 0
        ? { n: nonConfront, label: "Voci non confrontabili col benchmark" }
        : { n: 0, label: "Prezzi da verificare" });

  return (
    <div>
      <header className="max-w-2xl mx-auto px-5 py-6 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center"><Logo className="text-2xl" /></button>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#64748B]"><ShieldCheck className="w-4 h-4 text-[#3157F6]" strokeWidth={2} /> Anteprima gratuita</span>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-16">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${risk.cls}`} data-testid="risk-badge">
            <AlertTriangle className="w-4 h-4" strokeWidth={2.2} /> {risk.label}
          </span>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] leading-[1.12]">
            {punti > 0
              ? `Abbiamo trovato ${punti} ${punti === 1 ? "punto" : "punti"} da controllare`
              : `Abbiamo analizzato ${teaser.num_voci} voci`}
          </h1>
          <p className="mt-3 text-[#64748B] leading-relaxed">
            Ecco la sintesi gratuita del tuo preventivo. Sblocca l’analisi completa per vedere ogni dettaglio.
          </p>
        </motion.div>

        {conLimiti && (
          <div className="mt-5 rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4 flex items-start gap-3" data-testid="limiti-banner">
            <Info className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" strokeWidth={2} />
            <p className="text-sm text-[#7c4a03] leading-relaxed">
              {eligibility?.avviso || "Alcune voci sono generiche o \u201Ca corpo\u201D: le valutazioni di prezzo hanno confidenza ridotta e potrebbero non essere pienamente confrontabili."}
            </p>
          </div>
        )}

        {/* Score + importo */}
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm font-semibold text-[#64748B]">Completezza</p>
              <div className="mt-1 flex items-end gap-2">
                <span className="font-heading text-5xl font-semibold text-[#0F172A] leading-none" data-testid="teaser-score">{teaser.punteggio ?? "—"}</span>
                <span className="text-[#64748B] text-sm mb-1.5">/ 100</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-[#64748B]">Importo rilevato</p>
              <p className="font-heading text-2xl font-semibold text-[#0F172A] mt-1" data-testid="stat-totale">{fmtEuro(teaser.importo_totale)}</p>
            </div>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-[#E2E8F0] overflow-hidden">
            <div className="h-full rounded-full bg-[#3157F6]" style={{ width: `${teaser.punteggio ?? 0}%` }} />
          </div>
          <p className="mt-2 text-sm text-[#64748B]">{teaser.num_voci} voci analizzate</p>
        </div>

        {/* Blocchi conteggio */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Metric icon={PackageMinus} n={teaser.num_lavorazioni_mancanti} label="Possibili costi mancanti" testid="stat-mancanti" />
          <Metric icon={TrendingUp} n={prezzoMetric.n} label={prezzoMetric.label} testid="stat-prezzi" />
          <Metric icon={HelpCircle} n={teaser.num_voci_poco_chiare} label="Voci poco chiare" testid="stat-chiare" />
          <Metric icon={ReceiptText} n={teaser.num_extracosti} label="Potenziali extracosti" testid="stat-extra" />
        </div>

        {/* Criticità gratuita */}
        {teaser.criticita_gratuita && (
          <div className="mt-8">
            <p className="text-sm font-semibold text-[#3157F6] mb-3">Un punto emerso dall’analisi</p>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="free-finding">
              <div className="flex flex-wrap gap-2 mb-3" data-testid="free-finding-badges">
                <span className="inline-flex items-center rounded-full bg-[#3157F6]/10 text-[#2444D8] px-2.5 py-1 text-[11px] font-semibold">Prezzo da approfondire</span>
                <span className="inline-flex items-center rounded-full bg-[#0F172A]/5 text-[#0F172A] px-2.5 py-1 text-[11px] font-semibold">Confronto con benchmark</span>
                <span className="inline-flex items-center rounded-full bg-[#16A34A]/10 text-[#15803D] px-2.5 py-1 text-[11px] font-semibold">Prima criticità gratuita</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-[#0F172A]">{teaser.criticita_gratuita.titolo}</h3>
              <p className="mt-2 text-[#334155] leading-relaxed">{teaser.criticita_gratuita.descrizione}</p>
            </div>
          </div>
        )}

        {/* Bloccate */}
        <div className="mt-6 relative">
          <div className="space-y-3" aria-hidden="true">
            {locked.map((_, i) => (
              <div key={"locked-" + i} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 okkio-blur">
                <h3 className="font-heading font-semibold text-[#0F172A]">Criticità riservata #{i + 2}</h3>
                <p className="mt-1.5 text-sm text-[#64748B]">Questa voce richiede una verifica prima della firma…</p>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="rounded-2xl bg-white border border-[#E2E8F0] p-6 shadow-[0_16px_48px_-16px_rgba(15,23,42,0.25)] max-w-sm">
              <span className="w-12 h-12 rounded-xl bg-[#3157F6]/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-[#3157F6]" strokeWidth={2} />
              </span>
              <p className="font-heading text-lg font-semibold text-[#0F172A]">Altre {teaser.num_criticita_bloccate} criticità bloccate</p>
              <p className="text-sm text-[#64748B] mt-1.5">Sblocca prezzi, completezza, extracosti, ambiguità, domande e messaggio pronto per l’impresa.</p>

              <div className="mt-4 text-left rounded-xl bg-[#F7F9FC] border border-[#E2E8F0] p-3 text-xs text-[#475569] leading-relaxed" data-testid="precheckout-info">
                <p><span className="font-semibold text-[#0F172A]">Report completo OKKIO</span> — analisi completa del preventivo.</p>
                <p className="mt-1">Prezzo totale <span className="font-semibold text-[#0F172A]">€ 9,99</span>, pagamento una tantum (IVA inclusa ove applicabile). Fornitore: DamiDigital</p>
                <p className="mt-1">Report disponibile subito online e via email dopo il pagamento.</p>
              </div>

              <label className="mt-3 flex items-start gap-2 text-left cursor-pointer">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="precheckout-consent" className="mt-0.5 w-4 h-4 accent-[#3157F6] flex-shrink-0" />
                <span className="text-xs text-[#475569] leading-relaxed">
                  Chiedo l’esecuzione immediata del servizio e accetto di perdere il diritto di recesso una volta generato il report. Ho letto i{" "}
                  <Link to="/termini" className="font-semibold text-[#3157F6] hover:underline">Termini</Link> e la{" "}
                  <Link to="/privacy-policy" className="font-semibold text-[#3157F6] hover:underline">Privacy</Link>.
                </span>
              </label>

              <p className="mt-4 text-sm font-semibold text-[#0F172A]" data-testid="cta-microcopy">
                {teaser.num_criticita_bloccate > 0
                  ? `Sblocca le altre ${teaser.num_criticita_bloccate} criticità e il report completo`
                  : "Sblocca tutte le criticità e il report completo"}
              </p>
              <Button data-testid="unlock-cta" onClick={onUnlock} disabled={unlocking || !agreed}
                className="mt-2 w-full box-border inline-flex items-center justify-center gap-2 rounded-xl min-h-[3.75rem] px-5 py-4 text-base sm:text-lg font-bold leading-tight whitespace-normal text-white bg-[#3157F6] hover:bg-[#2444D8] active:scale-[0.98] shadow-[0_12px_32px_-10px_rgba(49,87,246,0.7)] transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none">
                <span>{unlocking ? "Attendere…" : "Paga €9,99 e sblocca il report completo"}</span>
                {!unlocking && <ArrowRight className="w-5 h-5 flex-shrink-0" />}
              </Button>
              <p className="text-xs text-[#64748B] mt-3 leading-relaxed">Pagamento unico · Nessun abbonamento · Pagamento sicuro con Stripe</p>
              <p className="text-xs text-[#94A3B8] mt-1">Link permanente al report via email</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
